import React, { useMemo } from 'react';
import { CommentSection, DoughnutGraph, RatingCard } from '../../organisms';
import {
  upselling,
  delivery,
  timebgyellow,
  india,
  network,
  price_drop_icon,
  WalletIcon,
  totalamont
} from '../../../../assets/images';
import MetricCard from '../../atoms/MetricCard';
import { SpinnerDotted } from '../../atoms';
import { commaingNumber } from '../../../../utils';
import WalletUtilizationCommon from './WalletUtilizationCommon';
import { UtilizationSankyChart } from '../../../tailwind/organisms';
import { PreferredPartner } from '../../molecules';

const Pharmacy = ({ metricObj, ratingObj, coverageObj, spentSavingsObj, loading, walletUtilizationObj, benefitType = 'pharmacy', benefitName, showNoUtilization }) => {
  const { totalOrderCount, averageTat, oneTimeUsers, repeat_users, totalAmountSpent = 0,
        healthCheckAvailedPercentage, successMetric, donutGraphData, partners, activeEmployeesAppointmentsCount, inactiveEmployeesAppointmentsCount,
        activeDependentsAppointmentsCount, inactiveDependentsAppointmentsCount, totalHealthChecksCompleted, unique_users, activeCustomerCount,
        activeEmployeesUpcomingAppointmentsCount, activeDependentsUpcomingAppointmentsCount, hasCustomerFormSubmissions,
        activeUniqueEmployeesAppointmentsCount = 0, inactiveUniqueEmployeesAppointmentsCount = 0,
        activeUniqueDependentsAppointmentsCount = 0, inactiveUniqueDependentsAppointmentsCount = 0
      } = metricObj;
  const { ratingsPercent, totalratings, averagerating, commentSectionData } = ratingObj;
  const { spend, savings } = spentSavingsObj;
  const { orders_delivered = {}, network_coverage = {} } = coverageObj;

  const sankyChartData = useMemo(() => {
    const employeesAppointmentsCount = activeEmployeesAppointmentsCount + activeDependentsAppointmentsCount
    const dependentsAppointmentsCount = inactiveEmployeesAppointmentsCount + inactiveDependentsAppointmentsCount
    const totalAppointmentsCount = employeesAppointmentsCount + dependentsAppointmentsCount

    const totalUnique = (activeUniqueEmployeesAppointmentsCount || 0) + (inactiveUniqueEmployeesAppointmentsCount || 0) +
      (activeUniqueDependentsAppointmentsCount || 0) + (inactiveUniqueDependentsAppointmentsCount || 0)
    const uniqueActiveBranch = (activeUniqueEmployeesAppointmentsCount || 0) + (activeUniqueDependentsAppointmentsCount || 0)
    const uniqueInactiveBranch = (inactiveUniqueEmployeesAppointmentsCount || 0) + (inactiveUniqueDependentsAppointmentsCount || 0)

    const logValue = (v) => {
      const scaled = Math.log((v || 1) + 1)
      return Math.max(0.8, scaled)
    }
    const v24 = logValue(activeUniqueEmployeesAppointmentsCount)
    const v25 = logValue(activeUniqueDependentsAppointmentsCount)
    const v36 = logValue(inactiveUniqueEmployeesAppointmentsCount)
    const v37 = logValue(inactiveUniqueDependentsAppointmentsCount)

    const LINE_BREAK = '__BR__'
    // First node: total users + completed appointments
    const point1 = `${commaingNumber(totalUnique)} Users${LINE_BREAK}(Delivered ${commaingNumber(totalAppointmentsCount || 0)} orders)`
    // Second node: Active / Inactive users + completed appointments
    const point2 = `${commaingNumber(uniqueActiveBranch)} Active Users${LINE_BREAK}(Delivered ${commaingNumber(employeesAppointmentsCount || 0)} orders)`
    const point3 = `${commaingNumber(uniqueInactiveBranch)} Inactive Users${LINE_BREAK}(Delivered ${commaingNumber(dependentsAppointmentsCount || 0)} orders)`
    // Third node under Active: Employees / Dependents
    const point4 = `${commaingNumber(activeUniqueEmployeesAppointmentsCount || 0)} Employees${LINE_BREAK}(Delivered ${commaingNumber(activeEmployeesAppointmentsCount || 0)} orders)`
    const point5 = `${commaingNumber(activeUniqueDependentsAppointmentsCount || 0)} Dependents${LINE_BREAK}(Delivered ${commaingNumber(activeDependentsAppointmentsCount || 0)} orders)`
    // Third node under Inactive: Employees / Dependents
    const point6 = `${commaingNumber(inactiveUniqueEmployeesAppointmentsCount || 0)} Employees${LINE_BREAK}(Delivered ${commaingNumber(inactiveEmployeesAppointmentsCount || 0)} orders)`
    const point7 = `${commaingNumber(inactiveUniqueDependentsAppointmentsCount || 0)} Dependents${LINE_BREAK}(Delivered ${commaingNumber(inactiveDependentsAppointmentsCount || 0)} orders)`

    const colorsArray = ['#0951E1', '#00A04F', '#C54B4F', '#1FBDC0', '#5163BA', '#4D6189', '#8895AF']
    const nodeDefs = [
      { id: point1, valueBefore: totalUnique, valueAfter: totalAppointmentsCount, colorIndex: 0 },
      { id: point2, valueBefore: uniqueActiveBranch, valueAfter: employeesAppointmentsCount, colorIndex: 1 },
      { id: point3, valueBefore: uniqueInactiveBranch, valueAfter: dependentsAppointmentsCount, colorIndex: 2 },
      { id: point4, valueBefore: activeUniqueEmployeesAppointmentsCount, valueAfter: activeEmployeesAppointmentsCount, colorIndex: 3 },
      { id: point5, valueBefore: activeUniqueDependentsAppointmentsCount, valueAfter: activeDependentsAppointmentsCount, colorIndex: 4 },
      { id: point6, valueBefore: inactiveUniqueEmployeesAppointmentsCount, valueAfter: inactiveEmployeesAppointmentsCount, colorIndex: 5 },
      { id: point7, valueBefore: inactiveUniqueDependentsAppointmentsCount, valueAfter: inactiveDependentsAppointmentsCount, colorIndex: 6 }
    ]
    const keptNodes = nodeDefs.filter((n) => !(n.valueBefore === 0 && n.valueAfter === 0))
    const keptIds = new Set(keptNodes.map((n) => n.id))

    const allLinks = [
      { source: point1, target: point2, value: v24 + v25 },
      { source: point1, target: point3, value: v36 + v37 },
      { source: point2, target: point4, value: v24 },
      { source: point2, target: point5, value: v25 },
      { source: point3, target: point6, value: v36 },
      { source: point3, target: point7, value: v37 }
    ]
    let links = allLinks.filter((link) => keptIds.has(link.source) && keptIds.has(link.target))

    // Recalculate link values so node size and arrow size match after removal (flow conservation)
    const outgoingSumBySource = new Map()
    for (const link of links) {
      const current = outgoingSumBySource.get(link.source) || 0
      outgoingSumBySource.set(link.source, current + link.value)
    }
    const keptNodesById = new Map(keptNodes.map((n) => [n.id, n]))
    links = links.map((link) => {
      const isTopLevel = link.source === point1
      if (isTopLevel) {
        const outgoingSum = outgoingSumBySource.get(link.target) || 0
        const targetNode = keptNodesById.get(link.target)
        const value = outgoingSum > 0 ? outgoingSum : (targetNode ? logValue(targetNode.valueBefore) : link.value)
        return { ...link, value }
      }
      return link
    })

    const data = {
      nodes: keptNodes.map((n) => ({ id: n.id })),
      links
    }
    const colors = keptNodes.map((n) => colorsArray[n.colorIndex])
    return { data, colors }
  }, [
    totalHealthChecksCompleted,
    activeEmployeesAppointmentsCount, inactiveEmployeesAppointmentsCount, activeDependentsAppointmentsCount, inactiveDependentsAppointmentsCount,
    activeUniqueEmployeesAppointmentsCount, inactiveUniqueEmployeesAppointmentsCount,
    activeUniqueDependentsAppointmentsCount, inactiveUniqueDependentsAppointmentsCount
  ])

  return (
    <div>
      <p className='text-gray-700 text-base font-semibold mt-8 mb-3 leading-none'>Pharmacy Orders Summary</p>
      <div className='flex flex-row justify-between'>
          { totalAmountSpent > 0 &&
          ( <MetricCard
                loading={loading}
                loaderHeight={120}
                svgsrc={totalamont}
                titleClasses='font-semibold text-gray-800'
                title={`Total amount spent on sponsored ${benefitName}`}
                subTitle='Calculated based on delivered orders only'
                metricClasses='flex mt-4 text-gray-500'
                metricValue={<span className='ml-10 font-semibold text-3xl text-gray-700 mt-3'>₹{commaingNumber(totalAmountSpent)}</span>}
                classes='w-1/2 inline-block mr-5'
              />)
          }
        <MetricCard
          loading={loading}
          loaderHeight={120}
          svgsrc={delivery}
          titleClasses='font-semibold text-gray-800'
          title='Total orders delivered'
          subTitle='All orders delivered for self and dependents'
          metricClasses='flex mt-4 text-gray-500'
          metricValue={<span className='ml-10 font-semibold text-3xl text-gray-700'>{commaingNumber(totalOrderCount)}</span>}
          classes='w-1/2 inline-block mr-5'
        />
        <MetricCard
          loading={loading}
          loaderHeight={120}
          svgsrc={timebgyellow}
          titleClasses='font-semibold text-gray-800'
          title='Average delivery TAT'
          subTitle='Average delivery turn around time'
          metricValue={<span className='ml-10 font-semibold  text-3xl text-gray-700'>{commaingNumber(averageTat)}</span>}
          metricValueSuffix='hours'
          metricClasses='flex mt-4 text-gray-500'
          classes='w-1/2 inline-block mr-5'
        />
      </div>

      { showNoUtilization == false && (
        <div>
          <p className='text-gray-600 text-base font-semibold mt-8 mb-3 leading-none'>Orders distribution</p>
          { loading
            ? (
              <div className='px-9 py-20 border bg-white border-gray-200 flex items-center justify-center' style={{ minHeight: 450 }}>
                <SpinnerDotted />
              </div>
              )
            : (
              <UtilizationSankyChart sankeyData={sankyChartData} classes='px-9 py-20 border bg-white border-gray-200' height={450} />
              )
          }
        </div>
      )}

      <WalletUtilizationCommon metricObj={metricObj}  loading={loading} benefitName={benefitName} walletUtilizationObj={walletUtilizationObj} uniqueUsers={oneTimeUsers} />
      { showNoUtilization == false && 
      (<div>
        <div className='flex flex-row justify-between mt-12'>
          <div className='flex flex-col w-1/2 mr-4'>
            <p className='text-gray-600 text-base font-semibold leading-none'>{benefitName} Delivery Modes</p>
            <p className='text-sm mb-1 mt-0.5 text-gray-600'>
              Distribution of delivered orders by mode
            </p>
            <DoughnutGraph
              loading={loading}
              loaderHeight={200}
              title={benefitName}
              graphData={donutGraphData}
              classes='h-full'
              heightOfLoader={200}
              id={benefitType}
            />
          </div>
          <div className='flex flex-col w-1/2'>
            <p className='text-gray-600 text-base font-semibold leading-none'>
              Top preferred partners
            </p>
            <p className='text-sm mb-1 mt-0.5 text-gray-600'>
              Ranks partner based on number of employees who selected them for pharmacy orders
            </p>
            <PreferredPartner loading={loading} loaderHeight={200} list={partners} header='Partners' classes='h-full' />
          </div>
        </div>
      </div>)}
      {hasCustomerFormSubmissions &&
        <p className='text-sm text-gray-600 mt-2 mb-0 italic leading-none'>
          *Also includes data from the form submitted by the customer
        </p>
      }

      {/* <div className='flex flex-row justify-between mt-12'>
        <div className='flex flex-col w-1/2 mr-4'>
          <span className='text-gray-700 text-base font-semibold mb-3 leading-none'>
            Amount spent on pharmacy orders
          </span>
          <MetricCard
            loading={loading}
            loaderHeight={120}
            svgsrc={upselling}
            title='Total pharmacy order value '
            metricValue={<span className='font-semibold text-3xl text-gray-700'>₹{commaingNumber(spend)}</span>}
            classes='inline-block'
          />
        </div>
        <div className='flex flex-col w-1/2'>
          <span className='text-gray-700 text-base font-semibold mb-3 leading-none'>
            Amount saved on pharmacy by ekincare
          </span>
          <MetricCard
            loading={loading}
            loaderHeight={120}
            svgsrc={price_drop_icon}
            title='Saving provided by ekincare'
            metricValue={<span className='font-semibold text-3xl text-gray-700'>₹{commaingNumber(savings)}</span>}
            classes='inline-block'
          />
        </div>
      </div> */}

      <div className='flex flex-row mt-12'>
        <div className='flex flex-col w-2/4 pr-4'>
          <div className='text-base font-semibold leading-none text-gray-700 leading-none mb-1'>
            Ratings and Reviews
          </div>
          <div className='text-sm mb-3 leading-2 text-gray-600'>
            These ratings are captured after completing delivery of pharmacy
          </div>
          <RatingCard
            loading={loading}
            loaderHeight={200}
            rating={averagerating}
            number_of_ratings={totalratings}
            five_star_rating={ratingsPercent && ratingsPercent[4]}
            four_star_rating={ratingsPercent && ratingsPercent[3]}
            three_star_rating={ratingsPercent && ratingsPercent[2]}
            two_star_rating={ratingsPercent && ratingsPercent[1]}
            one_star_rating={ratingsPercent && ratingsPercent[0]}
            classes='h-full'
          />
        </div>

        <div className='flex flex-col w-2/4'>
          <div className='text-base font-semibold leading-none text-gray-700 leading-none mb-1'>Top Comments</div>
          <div className='text-sm mb-3 leading-2 text-gray-600'>
            These reviews are captured after completing delivery of Pharmacy
          </div>
          <CommentSection loading={loading} loaderHeight={200} points={commentSectionData} classes='h-full' />
        </div>
      </div>
      {hasCustomerFormSubmissions &&
        <p className='text-sm text-gray-600 mt-2 mb-0 italic leading-none'>
          *Also includes data from the form submitted by the customer
        </p>
      }
    </div>
  );
};

export default Pharmacy;
